/*
 * Solethreads lace try-on — used by blocks/_product-laces.liquid.
 *
 * <solethreads-lace-tryon data-role="shoe">  "Try different laces" on a shoe page:
 *   loads this shoe (for the variant selected in the product form) with its mapped laces.
 * <solethreads-lace-tryon data-role="lace">  "Try on your shoe" on a lace product page:
 *   a searchable list of try-on shoes; picking one shows it with this product's lace variants.
 *
 * Shoe data comes from sections/solethreads-lace-tryon-results.liquid through the Section
 * Rendering API, so the page never contains the shoe catalogue. Changing a lace only swaps
 * the preview image inside the dialog: the entry's overlay_image, used as a complete
 * shoe-with-lace photo and shown on its own (no layering).
 *
 * Isolation from Glozin: the selected variant is read from
 * #product-form-<section id> input[name="id"] when the dialog opens, and nothing else of
 * Glozin's is touched (no listeners on its form, gallery, 360 viewer or popups). The dialog
 * is a native <dialog>, moved to <body> so the product column cannot affect it.
 *
 * Shoe page: "Add laces with my shoe" posts the selected shoe variant and the selected
 * lace variant to cart/add.js as two line items.
 * Lace page: "Buy this look" adds the selected lace alone, or the lace plus a shoe variant
 * the customer picks explicitly (colour, size, ...) from the shoe's <product url>.js data.
 * Both refresh and open Glozin's cart drawer the same way its <product-form> does
 * (assets/theme.js). Purchase does not depend on a lace having preview artwork.
 */
(() => {
  const TAG = "solethreads-lace-tryon";
  if (customElements.get(TAG)) return;

  const SECTION_ID = "solethreads-lace-tryon-results";
  const SEARCH_DELAY = 300;
  const SEARCH_MIN_LENGTH = 2;
  const LOCK_CLASS = "st-lace-scroll-lock";

  const TEXT = {
    loading: "Loading shoes…",
    loadError: "Couldn't load shoes. Please try again.",
    previewError: "Couldn't load the preview. Please try again.",
    noShoes: "No shoes are available to try on yet.",
    noResults: (query) => `No shoes found for “${query}”.`,
    noEntries: "No lace previews for this colour yet.",
    notMapped: () => "Preview unavailable for this colour yet.",
    overlayError: "This lace preview couldn't be loaded.",
    soldOut: "Sold out",
    noPreview: "No preview yet",
    previewUnavailable: "Preview unavailable",
    previewList: (titles) => `Preview: ${titles.join(", ")}`,
    stageLabel: (shoe, lace) => (lace ? `${shoe} with ${lace} laces` : shoe),
    shoeColour: (shoe, colour) => `${shoe}, ${colour}`,
    adding: "Adding...",
    chooseShoe: "Choose an available colour and size on the page first.",
    chooseLace: "Choose a lace colour first.",
    cartError: (reason) => `Couldn't add to your cart${reason ? `: ${reason}` : ". Please try again."}`,
    addLaces: "Add laces to cart",
    addLook: "Add laces + shoe to cart",
    laceSoldOut: "This lace colour is sold out.",
    shoeLoading: "Loading shoe options…",
    shoeLoadError: "Couldn't load the shoe's options. Please try again.",
    chooseOptions: (names) => `Choose a ${names.join(" and ").toLowerCase()} first.`,
    shoeSoldOut: "This shoe option is sold out. Choose another.",
    laceLine: (lace) => `+ ${lace} laces`,
    // Numeric size values are shown as UK sizes (e.g. "6" → "UK 6").
    optionLabel: (name, value) => (/size/i.test(name) && /^\d+(\.\d+)?$/.test(value) ? `UK ${value}` : value),
  };

  const routesRoot = () => window.Shopify?.routes?.root || "/";

  const readJSON = (element) => {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn("Lace try-on: invalid JSON.", error);
      return null;
    }
  };

  // Resolves to the loaded Image, or null.
  const preload = (src) =>
    new Promise((resolve) => {
      if (!src) return resolve(null);
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = src;
    });

  class SolethreadsLaceTryon extends HTMLElement {
    connectedCallback() {
      if (this.dialog) return;
      this.dialog = this.querySelector("[data-st-lace-dialog]");
      this.trigger = this.querySelector("[data-st-lace-open]");
      if (!this.dialog || !this.trigger) return;
      if (typeof this.dialog.showModal !== "function") {
        this.hidden = true;
        return;
      }

      this.role = this.dataset.role;
      const find = (selector) => this.dialog.querySelector(selector);
      this.els = {
        back: find("[data-st-lace-back]"),
        close: find("[data-st-lace-close]"),
        views: this.dialog.querySelectorAll("[data-st-lace-view]"),
        stage: find("[data-st-lace-stage]"),
        base: find("[data-st-lace-base]"),
        shoeName: find("[data-st-lace-shoe-name]"),
        laceName: find("[data-st-lace-lace-name]"),
        swatches: find("[data-st-lace-swatches]"),
        note: find("[data-st-lace-note]"),
        link: find("[data-st-lace-link]"),
        add: find("[data-st-lace-add]"),
        buy: find("[data-st-lace-buy]"),
        buyModes: this.dialog.querySelectorAll("[data-st-lace-mode]"),
        shoeOptions: find("[data-st-lace-shoe-options]"),
        summary: find("[data-st-lace-summary]"),
        buyNote: find("[data-st-lace-buy-note]"),
        buySubmit: find("[data-st-lace-buy-submit]"),
        searchForm: find("[data-st-lace-search-form]"),
        search: find("[data-st-lace-search]"),
        list: find("[data-st-lace-list]"),
        listStatus: find("[data-st-lace-list-status]"),
        more: find("[data-st-lace-more]"),
      };
      this.laceProduct = readJSON(find("[data-st-lace-laces]"));
      this.shoes = new Map();
      this.shoeCache = new Map();
      this.options = [];
      this.query = "";
      this.renderToken = 0;

      document.body.append(this.dialog);

      this.trigger.addEventListener("click", () => this.open());
      this.els.close.addEventListener("click", () => this.dialog.close());
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener("close", () => {
        document.documentElement.classList.remove(LOCK_CLASS);
        this.abortRequest();
      });
      this.els.swatches.addEventListener("click", (event) => {
        const swatch = event.target.closest("[data-lace-id]");
        if (swatch) this.selectLace(swatch.dataset.laceId);
      });

      if (this.els.add) {
        this.addLabel = this.els.add.textContent.trim();
        this.els.add.addEventListener("click", () => this.addPairToCart());
      }

      if (this.els.buy) {
        this.shoeChoices = [];
        // A chosen shoe colour only drives the preview in "Laces + shoe" mode.
        this.els.buyModes.forEach((input) =>
          input.addEventListener("change", () => (this.colourChoice() != null ? this.refreshLook() : this.updateBuy()))
        );
        this.els.shoeOptions.addEventListener("click", (event) => {
          const button = event.target.closest("[data-option-index]");
          if (button && !button.disabled) this.chooseShoeOption(Number(button.dataset.optionIndex), button.dataset.value);
        });
        this.els.buySubmit.addEventListener("click", () => this.buyLook());
      }

      if (this.role === "lace") {
        this.els.back.addEventListener("click", () => this.showList());
        this.els.list.addEventListener("click", (event) => {
          const button = event.target.closest("[data-st-lace-pick]");
          if (button) this.pickShoe(button);
        });
        this.els.search.addEventListener("input", () => {
          clearTimeout(this.searchTimer);
          this.searchTimer = setTimeout(() => this.runSearch(), SEARCH_DELAY);
        });
        this.els.searchForm.addEventListener("submit", (event) => {
          event.preventDefault();
          clearTimeout(this.searchTimer);
          this.runSearch();
        });
        this.els.more.addEventListener("click", () => this.loadPage(this.page + 1));
      }
    }

    disconnectedCallback() {
      this.abortRequest();
      if (this.dialog?.open) document.documentElement.classList.remove(LOCK_CLASS);
      this.dialog?.remove();
    }

    open() {
      this.dialog.showModal();
      document.documentElement.classList.add(LOCK_CLASS);

      if (this.role === "shoe") {
        this.loadCurrentShoe();
        return;
      }

      // The lace currently chosen on the page, read once.
      this.selectedLaceId = this.readVariantId();
      if (this.currentShoe) {
        this.setShoe(this.currentShoe);
        this.showView("preview");
      } else {
        this.showList();
      }
      if (!this.listLoaded) this.loadPage(1, { reset: true });
    }

    // Read-only: the variant Glozin's picker has written into its product form.
    readVariantId() {
      const form = document.getElementById(this.dataset.formId);
      const value = form?.querySelector('input[name="id"]')?.value || "";
      return /^\d+$/.test(value) ? value : null;
    }

    sectionUrl(path, params = {}) {
      const url = new URL(`${routesRoot()}${path}`, window.location.origin);
      Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
      url.searchParams.set("section_id", SECTION_ID);
      return `${url.pathname}${url.search}`;
    }

    abortRequest() {
      this.controller?.abort();
      this.controller = null;
    }

    // Resolves to { cards, page, pages }, { error: true }, or null when superseded.
    async fetchShoes(url) {
      this.abortRequest();
      const controller = new AbortController();
      this.controller = controller;
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const list = doc.querySelector("[data-st-lace-results-list]");
        if (!list) return { cards: [], page: 1, pages: 1 };
        const cards = Array.from(list.querySelectorAll("[data-st-lace-shoe]"))
          .map((card) => ({ card, data: readJSON(card.querySelector("[data-st-lace-shoe-data]")) }))
          .filter(({ data }) => data && data.id);
        return {
          cards,
          page: Number(list.dataset.page) || 1,
          pages: Number(list.dataset.pages) || 1,
        };
      } catch (error) {
        if (error.name === "AbortError") return null;
        console.warn("Lace try-on: request failed.", error);
        return { error: true };
      } finally {
        if (this.controller === controller) this.controller = null;
      }
    }

    /* ---------- Shoe page ---------- */

    async loadCurrentShoe() {
      const variantId = this.readVariantId();
      const key = variantId || "default";
      if (this.shoeCache.has(key)) {
        this.setShoe(this.shoeCache.get(key));
        return;
      }

      this.els.stage.classList.add("is-loading");
      this.setNote("");
      const url = this.sectionUrl(
        `products/${encodeURIComponent(this.dataset.productHandle)}`,
        variantId ? { variant: variantId } : {}
      );
      const result = await this.fetchShoes(url);
      if (!result) return;

      const shoe = result.cards?.[0]?.data;
      if (!shoe) {
        this.els.stage.classList.remove("is-loading");
        this.setNote(TEXT.previewError, "error");
        return;
      }
      this.shoeCache.set(key, shoe);
      this.setShoe(shoe);
    }

    /* ---------- Lace page ---------- */

    showList() {
      this.showView("shoes");
      this.lastPicked?.focus();
    }

    runSearch() {
      const value = this.els.search.value.trim();
      const query = value.length >= SEARCH_MIN_LENGTH ? value : "";
      if (query === this.query && this.listLoaded) return;
      this.query = query;
      this.loadPage(1, { reset: true });
    }

    async loadPage(page, { reset = false } = {}) {
      const query = this.query;
      const url = query
        ? this.sectionUrl("search", { q: query, type: "product", "options[prefix]": "last", page })
        : this.sectionUrl(`collections/${encodeURIComponent(this.dataset.collection)}`, { page });

      if (reset) {
        this.els.list.replaceChildren();
        this.shoes.clear();
        this.lastPicked = null;
      }
      this.els.more.hidden = true;
      this.els.list.setAttribute("aria-busy", "true");
      this.setListStatus(TEXT.loading);

      const result = await this.fetchShoes(url);
      if (!result) return;
      this.els.list.removeAttribute("aria-busy");
      if (result.error) {
        this.setListStatus(TEXT.loadError);
        return;
      }

      result.cards.forEach(({ card, data }) => {
        if (this.shoes.has(String(data.id))) return;
        this.shoes.set(String(data.id), data);
        this.describeCard(card, data);
        this.els.list.append(card);
      });
      this.listLoaded = true;
      this.page = result.page;

      const hasMore = result.page < result.pages;
      // Pages can come back empty when none of their shoes have overlays yet.
      if (!result.cards.length && hasMore) {
        this.loadPage(result.page + 1);
        return;
      }
      this.els.more.hidden = !hasMore;
      if (this.shoes.size) this.setListStatus("");
      else this.setListStatus(query ? TEXT.noResults(query) : TEXT.noShoes);
    }

    // Says which of this lace product's variants can be previewed on the shoe.
    describeCard(card, shoe) {
      const meta = card.querySelector("[data-st-lace-shoe-meta]");
      if (!meta || !this.laceProduct) return;
      const titles = [
        ...new Set(
          shoe.entries
            .filter((entry) => entry.current !== false && entry.lace.productId === this.laceProduct.productId)
            .map((entry) => entry.lace.title)
        ),
      ];
      meta.textContent = titles.length ? TEXT.previewList(titles) : TEXT.noPreview;
      card.toggleAttribute("data-unmapped", !titles.length);
    }

    pickShoe(button) {
      const shoe = this.shoes.get(button.dataset.stLacePick);
      if (!shoe) return;
      this.lastPicked = button;
      this.setShoe(shoe);
      this.showView("preview");
      this.els.back.focus();
    }

    /* ---------- Preview (both roles) ---------- */

    showView(name) {
      this.els.views.forEach((view) => {
        view.hidden = view.dataset.stLaceView !== name;
      });
      if (this.els.back) this.els.back.hidden = !(this.role === "lace" && name === "preview");
    }

    setShoe(shoe) {
      this.currentShoe = shoe;
      this.els.shoeName.textContent = shoe.title;

      // One entry per lace variant; an entry for this shoe colour beats a general one.
      this.entries = new Map();
      shoe.entries.forEach((entry) => {
        if (entry.current === false) return;
        const key = String(entry.lace.id);
        const existing = this.entries.get(key);
        if (!existing || (entry.specific && !existing.specific)) this.entries.set(key, entry);
      });

      if (this.role === "lace" && this.laceProduct) {
        this.options = this.laceProduct.variants.map((variant) => ({
          id: String(variant.id),
          title: variant.title,
          swatch: variant.swatch,
          available: variant.available,
          entry: this.entries.get(String(variant.id)) || null,
        }));
      } else {
        const products = new Set([...this.entries.values()].map((entry) => entry.lace.productId));
        this.options = [...this.entries.values()].map((entry) => ({
          id: String(entry.lace.id),
          title: entry.lace.title,
          subtitle: products.size > 1 ? entry.lace.product : "",
          swatch: entry.lace.swatch,
          available: entry.lace.available,
          entry,
        }));
      }
      if (this.els.buy) this.resetBuy();
      this.renderSwatches();

      let laceId = this.selectedLaceId;
      if (!this.options.some((option) => option.id === laceId)) {
        laceId = (this.options.find((option) => option.entry) || this.options[0])?.id || null;
      }
      this.selectLace(laceId);
    }

    renderSwatches() {
      const buttons = this.options.map((option) => {
        const entry = this.entryFor(option);
        const button = document.createElement("button");
        button.type = "button";
        button.className = "st-lace-swatch";
        button.dataset.laceId = option.id;
        button.setAttribute("aria-pressed", "false");
        if (!entry) button.dataset.unavailable = "";

        const media = document.createElement("span");
        media.className = "st-lace-swatch__media";
        if (option.swatch) {
          const image = document.createElement("img");
          image.src = option.swatch;
          image.alt = "";
          image.width = 64;
          image.height = 64;
          media.append(image);
        }

        const text = document.createElement("span");
        text.className = "st-lace-swatch__text";
        const label = document.createElement("span");
        label.className = "st-lace-swatch__label";
        label.textContent = option.title;
        text.append(label);

        const metaText = !option.available ? TEXT.soldOut : !entry ? TEXT.previewUnavailable : option.subtitle;
        if (metaText) {
          const meta = document.createElement("span");
          meta.className = "st-lace-swatch__meta";
          meta.textContent = metaText;
          text.append(meta);
        }

        button.append(media, text);
        return button;
      });
      this.els.swatches.replaceChildren(...buttons);
    }

    selectLace(laceId) {
      this.selectedLaceId = laceId;
      const shoe = this.currentShoe;
      const option = this.options.find((item) => item.id === laceId) || null;
      const entry = option ? this.entryFor(option) : null;
      const shoeVariant = this.previewShoeVariant();

      this.els.swatches.querySelectorAll("[data-lace-id]").forEach((swatch) => {
        swatch.setAttribute("aria-pressed", String(swatch.dataset.laceId === laceId));
      });
      this.els.laceName.textContent = option ? option.title : "";

      if (!option) this.setNote(this.role === "shoe" ? TEXT.noEntries : "");
      else if (!entry) this.setNote(TEXT.notMapped(option.title, shoe.title));
      else this.setNote("");

      if (this.els.link) {
        this.els.link.hidden = !entry;
        if (entry) this.els.link.href = entry.lace.url;
      }
      if (this.els.add) {
        this.els.add.hidden = !entry;
        this.els.add.disabled = !!entry && !entry.lace.available;
      }
      if (this.els.buy) this.updateBuy();

      const shoeTitle = shoeVariant ? TEXT.shoeColour(shoe.title, this.colourChoice()) : shoe.title;
      this.els.stage.setAttribute("aria-label", TEXT.stageLabel(shoeTitle, entry ? option.title : ""));
      // An entry's overlay_image is a complete preview (shoe + lace) and is shown by itself;
      // a lace without an entry shows the plain shoe photo — for "Laces + shoe", the photo of
      // the chosen colour (never the previous one; no photo hides the image).
      if (entry) this.renderStage({ src: entry.overlay }, true);
      else this.renderStage(shoeVariant ? this.variantImage(shoeVariant) : shoe.base, false);
    }

    /* ---------- Lace page: preview follows the "Laces + shoe" colour ---------- */

    colourIndex() {
      return this.shoeProduct ? this.shoeProduct.options.findIndex((item) => /colou?r/i.test(item.name)) : -1;
    }

    colourChoice() {
      const index = this.colourIndex();
      return index === -1 ? null : this.shoeChoices[index];
    }

    // The shoe variant the preview shows, resolved from the same shoeChoices the cart uses:
    // the exact variant once every option is chosen (= selectedShoeVariant()), otherwise a
    // variant of the chosen colour (available first). Null until a colour is chosen in
    // "Laces + shoe" mode, which keeps the default preview.
    previewShoeVariant() {
      if (!this.els.buy || this.buyMode() !== "pair" || this.colourChoice() == null) return null;
      const exact = this.selectedShoeVariant();
      if (exact) return exact;
      const colour = this.colourChoice();
      const index = this.colourIndex();
      const sameColour = this.shoeProduct.variants.filter((variant) => variant.options[index] === colour);
      const matching = sameColour.filter((variant) =>
        this.shoeChoices.every((choice, i) => choice == null || variant.options[i] === choice)
      );
      const pool = matching.length ? matching : sameColour;
      return pool.find((variant) => variant.available) || pool[0] || null;
    }

    // The entry to preview for a lace option: the default entry, or for a chosen
    // "Laces + shoe" colour an entry for that exact variant, then for its photo (colour),
    // then a general entry when that colour is the one the shoe card was rendered with.
    entryFor(option) {
      const variant = this.previewShoeVariant();
      if (!variant) return option.entry;
      const mediaId = variant.featured_media?.id;
      const entries = this.currentShoe.entries.filter((entry) => String(entry.lace.id) === option.id);
      return (
        entries.find((entry) => entry.shoeVariant === variant.id) ||
        entries.find((entry) => entry.shoeVariant && mediaId && entry.shoeMedia === mediaId) ||
        entries.find((entry) => !entry.shoeVariant && mediaId && mediaId === this.currentShoe.baseMedia) ||
        null
      );
    }

    // The variant's own photo, or another photo of the same colour; never Glozin's 360 frames.
    variantImage(variant) {
      const index = this.colourIndex();
      const candidates = [
        variant,
        ...this.shoeProduct.variants.filter((item) => index !== -1 && item.options[index] === variant.options[index]),
      ];
      const media = candidates
        .map((item) => item.featured_media)
        .find((item) => item?.preview_image?.src && item.alt !== "image-360");
      if (!media) return null;
      const url = new URL(media.preview_image.src, window.location.origin);
      url.searchParams.set("width", "1400");
      return { src: url.href, w: media.preview_image.width, h: media.preview_image.height };
    }

    // Re-renders swatch states and the preview for the current lace, mode and colour.
    refreshLook() {
      this.renderSwatches();
      this.selectLace(this.selectedLaceId);
    }

    // Shows one image in the stage, preloaded first so a slow response never replaces a newer choice.
    async renderStage(image, isPreview) {
      const token = ++this.renderToken;
      const { stage, base: stageImage } = this.els;
      stage.classList.add("is-loading");

      const loaded = await preload(image?.src);
      if (token !== this.renderToken) return;

      if (loaded) {
        if (stageImage.getAttribute("src") !== image.src) stageImage.src = image.src;
        const width = image.w || loaded.naturalWidth;
        const height = image.h || loaded.naturalHeight;
        if (width && height) stage.style.setProperty("--st-lace-ratio", `${width} / ${height}`);
        stageImage.hidden = false;
      } else {
        stageImage.hidden = true;
        if (isPreview) this.setNote(TEXT.overlayError, "error");
      }
      stage.classList.remove("is-loading");
    }

    /* ---------- Shoe page: add shoe + lace ---------- */

    async addPairToCart() {
      const button = this.els.add;
      if (button.getAttribute("aria-busy") === "true") return;

      // Glozin disables its own add button when the chosen colour/size is unavailable,
      // while input[name="id"] can still hold the previous variant, so check both.
      const form = document.getElementById(this.dataset.formId);
      const shoeVariantId = this.readVariantId();
      const glozinAdd = form?.querySelector('[name="add"]');
      if (!shoeVariantId || glozinAdd?.hasAttribute("disabled")) {
        this.setNote(TEXT.chooseShoe, "error");
        return;
      }
      const option = this.options.find((item) => item.id === this.selectedLaceId);
      if (!option?.entry) {
        this.setNote(TEXT.chooseLace, "error");
        return;
      }

      this.postToCart(
        [
          { id: Number(shoeVariantId), quantity: 1 },
          { id: Number(option.id), quantity: 1 },
        ],
        button,
        this.addLabel,
        (message) => this.setNote(message, message ? "error" : "")
      );
    }

    /* ---------- Lace page: buy this look ---------- */

    buyMode() {
      return [...this.els.buyModes].find((input) => input.checked)?.value || "laces";
    }

    // A new shoe starts with nothing chosen; its options load when "Laces + shoe" is picked.
    resetBuy() {
      this.shoeChoices = [];
      this.shoeProduct = null;
      this.els.shoeOptions.replaceChildren();
      this.setBuyNote("");
    }

    updateBuy() {
      const pair = this.buyMode() === "pair";
      this.els.shoeOptions.hidden = !pair;
      if (pair && !this.shoeProduct) this.loadShoeProduct();
      this.els.buySubmit.textContent = pair ? TEXT.addLook : TEXT.addLaces;

      const option = this.options.find((item) => item.id === this.selectedLaceId);
      this.els.buySubmit.disabled = !option || !option.available;
      if (option && !option.available) this.setBuyNote(TEXT.laceSoldOut);
      else if (this.els.buyNote.textContent === TEXT.laceSoldOut) this.setBuyNote("");
      this.renderSummary();
    }

    async loadShoeProduct() {
      const shoe = this.currentShoe;
      if (!shoe || this.shoeProductLoading === shoe.id) return;
      this.shoeProductLoading = shoe.id;
      this.els.shoeOptions.textContent = TEXT.shoeLoading;
      try {
        const url = new URL(`${shoe.url.split("?")[0]}.js`, window.location.origin);
        const response = await fetch(`${url.pathname}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const product = await response.json();
        if (this.currentShoe?.id !== shoe.id) return;
        this.shoeProduct = product;
        // Options with a single value need no choice (e.g. one-colour shoes).
        this.shoeChoices = product.options.map((option) => (option.values.length === 1 ? option.values[0] : null));
        this.renderShoeOptions();
        // A single-value colour is chosen already, so the preview follows it.
        if (this.colourChoice() != null) this.refreshLook();
        else this.renderSummary();
      } catch (error) {
        console.warn("Lace try-on: could not load shoe options.", error);
        if (this.currentShoe?.id === shoe.id) this.els.shoeOptions.textContent = TEXT.shoeLoadError;
      } finally {
        if (this.shoeProductLoading === shoe.id) this.shoeProductLoading = null;
      }
    }

    // True when some available variant has this value together with the other chosen values.
    isOptionAvailable(index, value) {
      return this.shoeProduct.variants.some(
        (variant) =>
          variant.available &&
          variant.options[index] === value &&
          this.shoeChoices.every((choice, i) => i === index || choice == null || variant.options[i] === choice)
      );
    }

    renderShoeOptions() {
      const product = this.shoeProduct;
      if (!product) return;
      const isDefault = product.options.length === 1 && product.options[0].values[0] === "Default Title";
      const groups = isDefault
        ? []
        : product.options.map((option, index) => {
            const fieldset = document.createElement("fieldset");
            fieldset.className = "st-lace-fieldset";
            const legend = document.createElement("legend");
            legend.className = "st-lace-panel__label";
            legend.textContent = option.name;
            if (this.shoeChoices[index] != null) {
              const value = document.createElement("span");
              value.className = "st-lace-panel__value";
              value.textContent = TEXT.optionLabel(option.name, this.shoeChoices[index]);
              legend.append(value);
            }
            // The colour option shows each colour's own variant photo, when the variants have photos.
            const thumbs = index === this.colourIndex() ? this.colourThumbs(index) : null;
            const list = document.createElement("div");
            list.className = thumbs ? "st-lace-opts st-lace-opts--colour" : "st-lace-opts";
            option.values.forEach((value) => {
              const button = document.createElement("button");
              button.type = "button";
              button.className = thumbs ? "st-lace-opt st-lace-opt--colour" : "st-lace-opt";
              button.dataset.optionIndex = index;
              button.dataset.value = value;
              const label = TEXT.optionLabel(option.name, value);
              if (thumbs) {
                const media = document.createElement("span");
                media.className = "st-lace-opt__media";
                if (thumbs.has(value)) {
                  const image = document.createElement("img");
                  image.src = thumbs.get(value);
                  image.alt = "";
                  image.width = 200;
                  image.height = 200;
                  image.decoding = "async";
                  media.append(image);
                }
                const name = document.createElement("span");
                name.className = "st-lace-opt__name";
                name.textContent = label;
                button.append(media, name);
              } else {
                button.textContent = label;
              }
              button.setAttribute("aria-pressed", String(this.shoeChoices[index] === value));
              if (!this.isOptionAvailable(index, value)) {
                button.disabled = true;
                button.setAttribute("aria-label", `${label}, ${TEXT.soldOut}`);
              }
              list.append(button);
            });
            fieldset.append(legend, list);
            return fieldset;
          });
      this.els.shoeOptions.replaceChildren(...groups);
    }

    // Colour value → thumbnail URL of that colour's variant photo, one per colour. Prefers the
    // variant matching the other chosen options (e.g. the selected size), then the colour's first
    // available variant, then any variant of the colour. Never Glozin's 360 frames. Null when
    // no variant has a photo.
    colourThumbs(index) {
      const variants = this.shoeProduct.variants;
      const photo = (variant) => {
        const media = variant.featured_media;
        return media?.preview_image?.src && media.alt !== "image-360" ? media.preview_image.src : null;
      };
      const matchesOthers = (variant) =>
        this.shoeChoices.every((choice, i) => i === index || choice == null || variant.options[i] === choice);
      const thumbs = new Map();
      this.shoeProduct.options[index].values.forEach((value) => {
        const ofColour = variants.filter((variant) => variant.options[index] === value && photo(variant));
        const variant =
          ofColour.find((item) => matchesOthers(item)) || ofColour.find((item) => item.available) || ofColour[0];
        if (!variant) return;
        const url = new URL(photo(variant), window.location.origin);
        url.searchParams.set("width", "200");
        thumbs.set(value, url.href);
      });
      return thumbs.size ? thumbs : null;
    }

    chooseShoeOption(index, value) {
      this.shoeChoices[index] = value;
      this.setBuyNote("");
      this.renderShoeOptions();
      // The preview reads the same shoeChoices as buyLook(), so it always shows the variant
      // that would be added (an entry can target an exact colour + size variant).
      if (this.colourChoice() != null) this.refreshLook();
      else this.renderSummary();
    }

    selectedShoeVariant() {
      if (!this.shoeProduct || this.shoeChoices.some((choice) => choice == null)) return null;
      return (
        this.shoeProduct.variants.find((variant) => variant.options.every((value, i) => value === this.shoeChoices[i])) ||
        null
      );
    }

    renderSummary() {
      const option = this.options.find((item) => item.id === this.selectedLaceId);
      const variant = this.buyMode() === "pair" ? this.selectedShoeVariant() : null;
      this.els.summary.hidden = !variant || !option;
      if (!variant || !option) return;

      const shoe = document.createElement("strong");
      shoe.textContent = this.currentShoe.title;
      const details = document.createElement("span");
      // e.g. "Black Aqua / UK 8": size last, whatever order the options are defined in.
      const parts = this.shoeProduct.options
        .map((item, i) => ({ name: item.name, value: variant.options[i] }))
        .filter((part) => part.value !== "Default Title");
      details.textContent = [
        ...parts.filter((part) => !/size/i.test(part.name)),
        ...parts.filter((part) => /size/i.test(part.name)),
      ]
        .map((part) => TEXT.optionLabel(part.name, part.value))
        .join(" / ");
      const lace = document.createElement("span");
      lace.textContent = TEXT.laceLine(option.title);
      this.els.summary.replaceChildren(shoe, details, lace);
    }

    buyLook() {
      const option = this.options.find((item) => item.id === this.selectedLaceId);
      if (!option) {
        this.setBuyNote(TEXT.chooseLace);
        return;
      }
      if (!option.available) {
        this.setBuyNote(TEXT.laceSoldOut);
        return;
      }

      const items = [];
      if (this.buyMode() === "pair") {
        if (!this.shoeProduct) {
          this.loadShoeProduct();
          return;
        }
        const variant = this.selectedShoeVariant();
        if (!variant) {
          const missing = this.shoeProduct.options.filter((item, i) => this.shoeChoices[i] == null).map((item) => item.name);
          this.setBuyNote(missing.length ? TEXT.chooseOptions(missing) : TEXT.shoeSoldOut);
          return;
        }
        if (!variant.available) {
          this.setBuyNote(TEXT.shoeSoldOut);
          return;
        }
        items.push({ id: variant.id, quantity: 1 });
      }
      items.push({ id: Number(option.id), quantity: 1 });

      const label = this.buyMode() === "pair" ? TEXT.addLook : TEXT.addLaces;
      this.postToCart(items, this.els.buySubmit, label, (message) => this.setBuyNote(message));
    }

    setBuyNote(message) {
      this.els.buyNote.textContent = message;
      this.els.buyNote.dataset.tone = message ? "error" : "";
    }

    /* ---------- Cart (both roles) ---------- */

    // Posts the items as separate lines, then opens Glozin's cart drawer.
    async postToCart(items, button, label, notify) {
      if (button.getAttribute("aria-busy") === "true") return;
      const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      const body = { items };
      if (typeof cart?.getSectionsToRender === "function") {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      notify("");
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.textContent = TEXT.adding;

      try {
        const response = await fetch(`${routesRoot()}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) {
          notify(TEXT.cartError(data.description || data.message));
          this.updateCartCount();
          return;
        }
        if (!cart) {
          window.location.href = `${routesRoot()}cart`;
          return;
        }
        this.dialog.close();
        this.openCartDrawer(cart, data);
      } catch (error) {
        console.warn("Lace try-on: add to cart failed.", error);
        notify(TEXT.cartError(""));
      } finally {
        button.textContent = label;
        button.removeAttribute("aria-busy");
        button.disabled = false;
      }
    }

    // Same steps as Glozin's <product-form> after a successful add.
    openCartDrawer(cart, data) {
      this.updateCartCount();
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "solethreads-lace-tryon", cartData: data });
      }
      if (data.sections) {
        cart.getSectionsToRender().forEach((section) => {
          const target = document.getElementById(section.id);
          const markup = data.sections[section.id];
          if (!target || !markup) return;
          const form = new DOMParser().parseFromString(markup, "text/html").querySelector("#minicart-form");
          if (form) target.innerHTML = form.innerHTML;
        });
      }
      if (typeof cart.cartAction === "function") cart.cartAction();
      cart.classList.remove("is-empty");
      if (typeof cart.setActiveElement === "function") cart.setActiveElement(this.trigger);
      setTimeout(() => {
        if (typeof showMinicartAddSuccessMessage === "function") showMinicartAddSuccessMessage(cart);
        if (typeof cart.open === "function") cart.open();
      }, 300);
    }

    updateCartCount() {
      fetch(`${routesRoot()}cart.js`)
        .then((response) => response.json())
        .then((cartData) => {
          if (cartData.item_count === undefined) return;
          document.querySelectorAll(".cart-count").forEach((element) => {
            element.innerHTML = element.classList.contains("cart-count-drawer")
              ? `(${cartData.item_count})`
              : cartData.item_count > 100
                ? "~"
                : cartData.item_count;
          });
          document.querySelector("header-total-price")?.updateTotal?.(cartData);
          document.querySelector("free-ship-progress-bar")?.init?.(cartData.items_subtotal_price);
        })
        .catch((error) => console.warn("Lace try-on: cart count update failed.", error));
    }

    setNote(message, tone = "") {
      this.els.note.textContent = message;
      this.els.note.dataset.tone = tone;
    }

    setListStatus(message) {
      this.els.listStatus.textContent = message;
    }
  }

  customElements.define(TAG, SolethreadsLaceTryon);
})();
